import { takeEvery, takeLatest, delay } from 'redux-saga'
import { put, fork, select, cancel, take, call, cancelled, all, spawn } from 'redux-saga/effects'

import actionTypes from 'redux/actions/actionTypes'
import { myLibrarySync, privateLibrarySync, previewSync, fetchShareDataSync, ltiSync } from '../../sync'
import callAny from './helpers/call_any'
import myLibraryActions from 'redux/actions/my_library_actions'
import googleDriveActions from 'redux/actions/googleDriveActions'
import cartActions from 'redux/actions/cartActions'
import myLibraryPendingLessonsSelector from 'redux/selectors/my_library/my_library_pending_lessons_selector'
import myLibraryPreviewSelector from 'redux/selectors/my_library/my_library_preview_selector'
import myLibraryViewSelector from 'redux/selectors/my_library/my_library_view_selector'
import { myLibrarySelectors, uiSelectors } from 'redux/selectors'
import { sortOptions } from 'helpers/search/sortOptions'
import { dateFormatFromTimestamp } from 'helpers/dateFormat/dateFormat'
import sessionTypes from 'helpers/enums/sessionTypes'
import libraryTypes from 'helpers/enums/libraryTypes'
import formatError from './helpers/error_formatter'
import googleAuthorizeUser from './helpers/google_authorize_user'
import lessonSources from 'helpers/enums/lessonSources'
import {createGoogleSlideTemplate} from 'helpers/googleDrive'
import launchLiveLessonHelper from './helpers/launch_live_lesson'
import launchHomeworkHelper from './helpers/launch_homework'
import myLibraryUserDataSelector from 'redux/selectors/my_library/my_library_userData_selector'
import googleSlidesUnauthorizedUserSelector from 'redux/selectors/my_library/google_slides_unauthorized_user_selector'
import lessonStatusTypes from 'helpers/enums/lessonStatusTypes'
import lessonTypes from 'helpers/enums/lessonTypes'
import { NearpodError } from 'redux/sagas/helpers/error_formatter'
import errorCodes from 'helpers/enums/errorCodes'
import upgradeSourceTypes from 'helpers/enums/metrics/upgradeSourceTypes'
import localErrorCodes from 'helpers/enums/errors/localErrorCodes'

const LESSONS_PER_PAGE = 20

function *getMyLibrary() {
	while (true) {
		try {
			const libraryData = yield select(
				myLibrarySelectors.myLibraryDataSelector
			)
			const order_field = libraryData.searchData.order_field
			if (
				!libraryData.searchData.order_field
				&& !libraryData.searchData.input_value
				&& !libraryData.searchData.more
			) {
				return
			}
			let orderField, orderType
			if (order_field >= 0 && order_field < sortOptions.length) {
				orderField = sortOptions[order_field].value
				orderType = sortOptions[order_field].type
			}
			const response = yield callAny(
				myLibrarySync.getUserLibrary,
				LESSONS_PER_PAGE,
				libraryData.searchData.page,
				libraryData.searchData.input_value,
				orderField,
				orderType,
				libraryData.folderUID,
				libraryData.searchData.fromLTI,
				libraryData.searchData.lessonType,
				libraryData.showAllLessons,
			)
			yield put(
				myLibraryActions.lessonsFetched(
					response.payload,
					true,
					libraryData.libraryType
				)
			)
		} catch (error) {
			const serverError = formatError(error)
			yield put(myLibraryActions.lessonsFailed(serverError))
		}
		yield take([
			actionTypes.MY_LIBRARY_GET_LESSONS_NEXT_PAGE,
			actionTypes.VIDEO_MY_VIDEOS_GET_LESSONS_NEXT_PAGE,
		])
	}
}

function *getLibraryPage() {
	let current_request
	while (true) {
		//TODO: handle my library finalize action to cancel fork but not create a new fork
		const action = yield take([
			actionTypes.MY_LIBRARY_INITIALIZE,
			actionTypes.MY_LIBRARY_SEARCH_LESSONS,
			actionTypes.MY_LIBRARY_LESSONS_PICKER_SAVE_LESSONS_SUCCESS,
			actionTypes.MY_LIBRARY_ROUTE_UPDATE,
			actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_ADD_LESSON_COMPLETED,
			actionTypes.MY_LIBRARY_FIRST_LESSON_PRESENTATION_PUBLISHED,
			actionTypes.MY_ACTIVITIES_INITIALIZE,
			actionTypes.VIDEO_MY_VIDEOS_PICKER_INITIALIZED,
			actionTypes.VIDEO_MY_VIDEOS_PICKER_QUERY_SEARCH,
		])
		if (current_request) {
			yield cancel(current_request)
		}
		current_request = yield fork(getMyLibrary, action)

	}
}

function *getMyLibraryFolders() {
	try {
		const libraryData = yield select(myLibrarySelectors.myLibraryDataSelector)
		const response = yield callAny(myLibrarySync.getUserLibraryFolders, libraryData.folderUID, libraryData.searchData.fromLTI)
		yield put(myLibraryActions.foldersFetched(response.payload))
	} catch (error) {
		// console.log(error)
	}
}

function *launchLTISession(action, sessionType) {
	try {
		const libraryLaunchLessonSelector = yield select(myLibrarySelectors.myLibraryLaunchLessonSelector)
		const response = yield callAny(ltiSync.saveCanvas, action.presentation_uid, sessionType, libraryLaunchLessonSelector.uid, libraryLaunchLessonSelector.ltiForwardUrl)
		yield put(myLibraryActions.myLibrarySessionLTICanvasSaved(response.payload, action.presentation_uid, sessionType, libraryLaunchLessonSelector.libraryType === libraryTypes.TEAMS))
	} catch (error) {
		yield put(myLibraryActions.myLibraryLTISessionFailed(formatError(error), action.presentation_uid))
	}
}

function *launchHomeworkWithCollaborateDisabled(action) {
	yield call(launchHomework, action, false)
}

function *launchHomework(action, validateCollaborate) {
	let isNew = action.isNew && action.isNew === '1'
	try {
		const newActionTypes = [
			actionTypes.MY_LIBRARY_LAUNCH_NEW_HOMEWORK,
			actionTypes.MY_LIBRARY_PREVIEW_LAUNCH_STUDENT_PACED,
		]
		isNew = newActionTypes.includes(action.type) || isNew ? '1' : '0'
		const response = yield call(launchHomeworkHelper, action.presentation_uid, isNew, '', validateCollaborate)
		yield put(myLibraryActions.myLibraryHomeworkFetched(response.payload.result, action.presentation_uid, isNew))
	} catch (error) {
		yield put(myLibraryActions.myLibraryHomeworkFailed(formatError(error), action.presentation_uid, isNew))
	}
}

function *launchExtensionHomework(action) {
	try {
		const response = yield call(launchHomeworkHelper, action.presentation_uid, '1', 'Google Classroom', false)
		if (response.payload.result.hwTrial && response.payload.result.hwTrial.showMessage === '1') {
			yield put(myLibraryActions.myLibraryExtensionPremiumFeatureUpgrade(response.payload.result))
		} else {
			yield put(myLibraryActions.myLibraryExtensionSessionFetched(
				action.presentation_uid,
				response.payload.result.messages.googleClassroom,
				'Student Paced',
				response.payload.result.isTutorial)
			)
		}
	} catch (error) {
		yield put(myLibraryActions.myLibraryExtensionSessionFailed(error.serverError))
	}
}

function *saveLessonBeforeLaunch(presentationUid) {
	const libraryLessonSelector = yield select(myLibrarySelectors.myLibraryLessonSelector, {entityUID: presentationUid})
	if (libraryLessonSelector.data.isStore) {
		if (libraryLessonSelector.data.myLibraryEntityUID !== '') {
			return libraryLessonSelector.data.myLibraryEntityUID
		} else {
			yield put(myLibraryActions.downloadAndLaunchStarted())
			yield put(
				cartActions.buyProduct(
					presentationUid,
					libraryLessonSelector.library_object_type === '1' ? 'P' : 'B',
					libraryLessonSelector.data.myLibraryEntityUID
				)
			)
			const errorActions = [
				actionTypes.CART_ITEM_FAILED,
			]
			const buyProductResultAction = yield take([
				...errorActions,
				actionTypes.CART_BUY_PRODUCT_SUCCESS,
			])
			yield put(myLibraryActions.downloadFinishedAndLaunchStarted())
			if (errorActions.includes(buyProductResultAction.type)) {
				throw new NearpodError()
			} else {
				yield spawn(getPreviewData, presentationUid, 'uid')
				return buyProductResultAction.payload.presentation.data.presentation_uid
			}
		}
	}
	return presentationUid
}

function *launchMyLibraryHomework(action) {
	try {
		const libraryLessonSelector = yield select(
			myLibrarySelectors.myLibraryLessonSelector,
			{entityUID: action.presentation_uid}
		)
		let presentationUid = ''
		if (libraryLessonSelector.data.inLibrary) {
			presentationUid = action.presentation_uid
		} else {
			presentationUid = yield call(saveLessonBeforeLaunch, action.presentation_uid)
		}
		const launchSessionData = {
			...action,
			presentation_uid: presentationUid,
		}
		const libraryLaunchLessonSelector = yield select(myLibrarySelectors.myLibraryLaunchLessonSelector)
		switch (libraryLaunchLessonSelector.libraryType) {
			case libraryTypes.LTI:
			case libraryTypes.TEAMS:
				yield launchLTISession(launchSessionData, sessionTypes.HOMEWORK)
				break
			case libraryTypes.EXTENSION:
				yield launchExtensionHomework(launchSessionData)
				break
			default:
				yield launchHomework(launchSessionData, true)
				break
		}
	} catch (error) {
		// console.log(error)
	}
}

function *launchLiveLesson(action) {
	//TODO: device_type and device_uid arguments
	try {
		const newActionTypes = [
			actionTypes.MY_LIBRARY_LAUNCH_NEW_LIVE_SESSION,
			actionTypes.MY_LIBRARY_PREVIEW_LAUNCH_ALL_DEVICES,
		]
		const isNew = newActionTypes.includes(action.type)
		const response = yield call(launchLiveLessonHelper, action.presentation_uid, isNew)
		const hasZoom = !!action.hasZoom
		yield put(myLibraryActions.myLibrarySessionFetched(response, action.presentation_uid, isNew, hasZoom))
	} catch (error) {
		yield put(myLibraryActions.myLibraryLiveSessionFailed(formatError(error), action.presentation_uid))
	}
}

function *launchExtensionLesson(action) {
	//TODO: device_type and device_uid arguments
	try {
		const response = yield call(launchLiveLessonHelper, action.presentation_uid, true, 'Google Classroom')
		const pinResponse = yield callAny(myLibrarySync.myLibrarySharePin, response.pincode, action.presentation_uid)
		yield put(myLibraryActions.myLibraryExtensionSessionFetched(
			action.presentation_uid,
			pinResponse.payload.messages.googleClassroom,
			'Live Session',
			response.isTutorial)
		)
	} catch (error) {
		yield put(myLibraryActions.myLibraryExtensionSessionFailed(error.serverError))
	}
}

function *launchMyLibraryLiveLesson(action) {
	try {
		const libraryLessonSelector = yield select(
			myLibrarySelectors.myLibraryLessonSelector,
			{entityUID: action.presentation_uid}
		)
		let presentationUid = ''
		if (libraryLessonSelector.data.inLibrary) {
			presentationUid = action.presentation_uid
		} else {
			presentationUid = yield call(saveLessonBeforeLaunch, action.presentation_uid)
		}
		const launchSessionData = {
			...action,
			presentation_uid: presentationUid,
		}
		const libraryLaunchLessonSelector = yield select(myLibrarySelectors.myLibraryLaunchLessonSelector)
		switch (libraryLaunchLessonSelector.libraryType) {
			case libraryTypes.LTI:
			case libraryTypes.TEAMS:
				yield launchLTISession(launchSessionData, sessionTypes.LIVE_SESSION)
				break
			case libraryTypes.EXTENSION:
				yield launchExtensionLesson(launchSessionData)
				break
			default:
				yield launchLiveLesson(launchSessionData)
				break
		}
	} catch (error) {
		// console.log(error)
	}
}

function *requestExtraLesson() {
	const libraryData = yield select(myLibrarySelectors.myLibraryDataSelector)
	const get_lesson_response = yield callAny(
		myLibrarySync.getUserLibrary,
		1,
		libraryData.searchData.page * LESSONS_PER_PAGE - 1,
		libraryData.searchData.searchString,
		libraryData.searchData.orderField,
		libraryData.searchData.orderType,
		libraryData.folderUID,
		libraryData.searchData.fromLTI,
		libraryData.searchData.lessonType,
	)
	yield put(
		myLibraryActions.lessonsFetched(
			get_lesson_response.payload,
			false
		)
	)
}

function *deleteLesson(action) {
	try {
		yield callAny(myLibrarySync.deleteLesson, action.lesson.data.entityUID)
		yield put(myLibraryActions.deleteLessonSuccess(action.lesson))
		// Adding extra lesson to page to compensate removal of last lesson
		yield fork(requestExtraLesson)
	} catch (error) {
		yield put(myLibraryActions.deleteLessonFailed(action.lesson))
	}
}

function *saveChanges(action) {
	//call server to get response
	try {
		if (
			action.entity.data.sourceInfo.type === lessonSources.NEARPOD
            || action.type === actionTypes.MY_LIBRARY_LESSON_DATA_SAVED
		) {
			const response = yield callAny(myLibrarySync.saveChanges, action.entity.data.entityUID)
			yield put(myLibraryActions.saveChangesFetched(response.payload.result[0], action.entity))
		}
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.saveChangesFailed(serverError, action.entity))
	}
}

function *getLessonsStatus() {
	while (true) {
		yield take([actionTypes.MY_LIBRARY_SAVE_CHANGES_FETCHED, actionTypes.MY_LIBRARY_LESSONS_FETCHED])
		try {
			let arePendingLessons = true
			while (arePendingLessons) {

				const pendingData = yield select(myLibraryPendingLessonsSelector)

				if (pendingData.pending_lessons.length !== 0) {
					const response = yield callAny(myLibrarySync.getLessonsStatus, pendingData.pending_lessons.join(','))
					yield put(myLibraryActions.pendingLessonsStatusFetched(response.payload.result))
					yield call(delay, 5000)
				} else {
					arePendingLessons = false
				}
			}
		} catch (error) {
			// console.log(error)
		}
	}
}

function *addToPublicLibrary(action) {
	try {
		yield callAny(myLibrarySync.addToPublicLibrary, action.lesson.data.id)
		yield put(myLibraryActions.addToPublicLibrarySuccess(action.lesson))
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.addToPublicLibraryFailed(serverError, action.lesson))
	}
}

function *addToFolder(action) {
	try {
		yield callAny(myLibrarySync.addLessonsToFolder, action.lesson, action.folderUID)
		// completing library list with extra lesson
		yield fork(requestExtraLesson)
		yield put(myLibraryActions.addToFolderSuccess(action.folderUID))
	} catch (error) {
		yield put(myLibraryActions.addToFolderFailed(error, action.lesson))
	}
}

function *moveLesson(action) {
	try {
		yield callAny(myLibrarySync.addLessonsToFolder, action.lessonUID, action.folderUID)
		// completing library list with extra lesson
		yield fork(requestExtraLesson)
		yield put(myLibraryActions.moveLessonSuccess())
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.moveLessonFailed(serverError))
	}
}

function *moveFolder(action) {
	try {
		yield callAny(myLibrarySync.addOrUpdateFolder, action.folderID, action.sourceName, null, action.targetFolderID)
		yield put(myLibraryActions.moveFolderSuccess())
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.moveFolderFailed(serverError))
	}
}

function *removeFromFolder(action) {
	try {
		const folderUID = yield select(myLibrarySelectors.myLibraryCurrentFolderSelector)
		yield callAny(myLibrarySync.removeLessonsFromFolder, action.lesson.data.entityUID, folderUID)
		// completing library list with extra lesson
		yield fork(requestExtraLesson)
		yield put(myLibraryActions.removeFromFolderSuccess(action.lesson))
	} catch (error) {
		yield put(myLibraryActions.removeFromFolderFailed(error, action.lesson))
	}
}

function *getLessonPdfUrl(action) {
	try {
		const response = yield callAny(myLibrarySync.getLessonPdfUrl, action.lesson.data.entityUID)
		yield put(myLibraryActions.exportToLocalDriveFetched(response.payload.pdfUrl))
	} catch (error) {
		// console.log(error)
	}
}

function *addOrUpdateFolder(action) {
	try {
		const presentationsUID = action.lesson ? action.lesson.data.entityUID : ''
		const response = yield callAny(myLibrarySync.addOrUpdateFolder, action.folder, action.folder_name, presentationsUID, action.parentFolder)
		yield put(myLibraryActions.addOrUpdateFolderSuccess(response, action))
	} catch (error) {
		if (error.serverError) {
			yield put(myLibraryActions.addOrUpdateFolderFailed(error.serverError))
		}
	}
}

function *changeFolderColor(action) {
	try {
		const changeFolderColorSelector = yield select(myLibraryViewSelector)
		const response = yield callAny(
			myLibrarySync.addOrUpdateFolder,
			changeFolderColorSelector.folder.id,
			changeFolderColorSelector.folder.name,
			null,
			changeFolderColorSelector.folder.id,
			action.colorId)
		yield put(myLibraryActions.changeFolderColorSuccess(
			response.payload
		))
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.changeFolderColorError(serverError))
	}
}

function *addToPrivateLibrary(action) {
	try {
		if (action.lesson.data.state === 1) {
			yield put(myLibraryActions.addToPrivateLibraryStarted())
			const library_uid = action.library_type === 'school' ? action.lesson.data.actions.canAddToSchoolLibraryId : action.lesson.data.actions.canAddToDistrictLibraryId
			yield callAny(privateLibrarySync.searchSaveEntities,
				library_uid, action.folderId, false, [action.lesson.data.id])
			yield put(myLibraryActions.addToPrivateLibrarySuccess(action.library_type, library_uid, action.lesson))
		}
	} catch (error) {
		if (error.serverError) {
			yield put(myLibraryActions.addToPrivateLibraryFailed(error.serverError, action.library_type, action.lesson))
		}
	}
}

function *removeFolder(action) {
	try {
		const response = yield callAny(myLibrarySync.removeFolder, action.folder.id)
		yield put(myLibraryActions.deleteFolderResponseSuccess(response.payload, action.folder.name))
	} catch (error) {
		yield put(myLibraryActions.deleteFolderResponseFailed())
	}
}

function *duplicateLessonConfirmed(action) {
	try {
		const response = yield callAny(myLibrarySync.editLesson, action.lessonData.entityUID, action.lessonData.folderId)
		yield put(myLibraryActions.duplicateLessonSuccess(response.payload.redirectUrl))
	} catch (error) {
		if (error && error.serverError) {
			yield put(
				myLibraryActions.myLibraryDuplicateLessonFailed(
					error.serverError,
					upgradeSourceTypes.DUPLICATE_PRESENTATION,
				)
			)
		}
	}
}

function *duplicateLesson(action) {
	try {
		if (action.lessonData.sourceInfo.type !== lessonSources.GOOGLE_SLIDES) {
			yield call(duplicateLessonConfirmed, action)
		}
	} catch (err) {}
}

function *editLesson(action) {
	try {
		const isEditRunning =
			(action.type === actionTypes.MY_LIBRARY_EDIT_RUNNING_LESSON)
		const isMyLibNotRun =
			(action.type === actionTypes.MY_LIBRARY_EDIT_STORE_LESSON && !action.lessonData.is_running)
		const isPubLibNotStoreNotRun = (
			action.type === actionTypes.MY_LIBRARY_EDIT_LESSON_IN_PUBLIC_LIBRARY
			&& !action.lessonData.isStore
			&& !action.lessonData.is_running
		)
		const isStoreNotRunNotPubLibPublished = (
			!action.lessonData.isStore
			&& !action.lessonData.is_running
			&& !action.lessonData.inPublicLibrary
			&& action.lessonData.state === lessonStatusTypes.PUBLISHED
		)
		const isGoogleSlidesEdit =
			(action.type === actionTypes.MY_LIBRARY_EDIT_GOOGLE_SLIDES_IN_NEARPOD)

		if (
			isEditRunning
			|| isMyLibNotRun
			|| isPubLibNotStoreNotRun
			|| isStoreNotRunNotPubLibPublished
			|| isGoogleSlidesEdit
		) {

			const isGoogleSlidesBlocked = yield select(googleSlidesUnauthorizedUserSelector)
			const isSilverGoogleSlide =
			(
				action.lessonData.sourceInfo.type === lessonSources.GOOGLE_SLIDES
				&& isGoogleSlidesBlocked
			)

			if (isSilverGoogleSlide && !isGoogleSlidesEdit) {
				throw new NearpodError(localErrorCodes.GOOGLE_SLIDES_UNAUTHORIZED_USER)
			} else {
				yield put(myLibraryActions.editLessonInit(action.lessonData, isGoogleSlidesEdit))
				const response = yield callAny(myLibrarySync.editLesson, action.lessonData.entityUID, action.lessonData.folderId, 'true')
				yield put(myLibraryActions.editLessonSuccess(response.payload.redirectUrl))
			}
		} else if (action.lessonData.state === lessonStatusTypes.NOT_PUBLISHED
			&& action.lessonData.type === lessonTypes.VIDEO) {
			const response = yield callAny(myLibrarySync.editUnpublishedVideoLesson, action.lessonData.entityUID)
			yield put(myLibraryActions.editLessonSuccess(response.payload.redirectUrl))
		}

	} catch (error) {
		const serverError = formatError(error)
		yield put(
			myLibraryActions.editLessonFailed(
				serverError,
				upgradeSourceTypes.DUPLICATE_PRESENTATION,
				action.lessonData,
			)
		)
	}
}

function *doSearchLessons(searchParams) {
	try {
		if (searchParams.more) {
			const response = yield callAny(myLibrarySync.searchLessonsForPicker, 20, searchParams.page, searchParams.search, searchParams.order_field, searchParams.order_type, searchParams.folderUID)
			yield put(myLibraryActions.lessonsPickerSearchLessonsFetched(response.payload.result))
		}
	} catch (err) {

	} finally {
		if (yield cancelled()) {
		}
	}
}

function *lessonsPickerSearchLessons() {
	let currentSearchParams, currentRequest
	while (true) {
		yield take([actionTypes.MY_LIBRARY_LESSONS_PICKER_SEARCH_LESSONS, actionTypes.MY_LIBRARY_LESSONS_PICKER_INIT])
		try {
			const searchParams = yield select(myLibrarySelectors.myLibraryLessonsPickerSelector)
			if (searchParams === currentSearchParams) {
			} else {
				currentSearchParams = searchParams
				if (currentRequest) {
					yield cancel(currentRequest)
				}
				currentRequest = yield fork(doSearchLessons, searchParams)
			}
		} catch (error) {
			// console.log(error)
		}
	}
}

function *lessonsPickerSaveLessons() {
	try {
		const saveParams = yield select(myLibrarySelectors.lessonsPickerModalSave)
		yield callAny(myLibrarySync.addLessonsToFolder, saveParams.lessons, saveParams.folderUID)
		const lessonsArray = saveParams.lessons.split(',')
		yield all(lessonsArray.map(() => put(myLibraryActions.lessonsPickerSaveLessonsSuccess(saveParams.folderUID))))
	} catch (error) {
		// console.log(error)
	}
}


function *getPreview() {
	const previewData = yield select(myLibraryPreviewSelector)
	yield call(getPreviewData, previewData.id, 'id')
}

function *getPreviewData(previewId, idType) {
	try {
		const response = yield callAny(previewSync.getPreview, previewId, idType)
		yield put(myLibraryActions.previewDataFetched(response.payload))
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.previewDataFetchedFailed(serverError))
	}
}

function *watchPreviewRoute() {
	let current_request
	while (true) {
		yield take([actionTypes.MY_LIBRARY_PREVIEW_INITIALIZE, actionTypes.MY_LIBRARY_PREVIEW_UPDATE])
		if (current_request) {
			yield cancel(current_request)
		}
		current_request = yield fork(getPreview)
	}
}

function *getMyLibraryLessonShareData(action) {
	try {
		const response = yield callAny(fetchShareDataSync, action.entityUID, action.entityType, action.libraryUID)

		yield put({
			type: actionTypes.SHARE_VIEW_DATA_LOADED,
			payload: response.payload.data,
			entityType: action.entityType,
			entityUID: action.entityUID,
		})
	} catch (error) {
		yield put({
			type: actionTypes.SHARE_VIEW_DATA_FAILED,
		})
	}
}

function *saveExpirationDate(action) {
	try {
		yield callAny(myLibrarySync.saveExpirationDate, dateFormatFromTimestamp(action.newExpireTime, 'YYYY-MM-DD'), action.sessionUid)
		yield put(myLibraryActions.saveExpirationDateSuccess())
	} catch (error) {
		yield put(myLibraryActions.saveExpirationDateFailed(action.sessionUid, action.currentExpireTime))
	}
}

function *saveSessionSubmissionRequired(action) {
	try {
		const userData = yield select(myLibraryUserDataSelector)
		yield callAny(myLibrarySync.saveSessionSubmissionRequired, action.value, action.sessionUid, userData.jwt)
		yield put(myLibraryActions.saveSessionSubmissionSuccess())
	} catch (error) {
		yield put(myLibraryActions.saveSessionSubmissionFailed(formatError(error), action.sessionUid, action.value))
	}
}

function *getMyLibraryShareFolderData(action) {
	try {
		const { entityUID, userId } = action
		const response = yield callAny(myLibrarySync.shareNPPFolder, entityUID, userId)
		yield put(myLibraryActions.shareFolderDataLoaded(response.payload.result, entityUID))
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.shareFolderDataFailed(serverError))
	}
}

function *updateLessonData(action) {
	const lessonsPublished = action.pending_lessons.filter(lesson => lesson.status === 1)
	yield all(lessonsPublished.map(lesson => call(getPreviewData, lesson.id, 'uid')))
}

function *createGoogleDriveLesson() {
	try {
		const isGoogleSlidesBlocked = yield select(googleSlidesUnauthorizedUserSelector)
		if (isGoogleSlidesBlocked) {
			throw new NearpodError(localErrorCodes.GOOGLE_SLIDES_UNAUTHORIZED_USER)
		}

		yield call(googleAuthorizeUser)
		const uiData = yield select(uiSelectors.uiDataSelector)

		const isInIframe = uiData && uiData.isInIframe

		yield put(googleDriveActions.googleCreateLessonAuthorizeSuccess(isInIframe))

		const lessonData = yield call(createGoogleSlideTemplate)

		yield callAny(myLibrarySync.linkGoogleSlideWithNearpodLesson, lessonData.id, lessonData.name)

		yield put(myLibraryActions.createGoogleDriveLessonSuccess(lessonData, isInIframe))
	} catch (err) {
		const serverError = formatError(err)
		yield put(myLibraryActions.createGoogleDriveLessonFailed(serverError))
	}
}

// code repeated on private_library_sagas and store_sagas
function *launchSession(presentationUID, sessionType) {
	try {
		const userData = yield select(myLibraryUserDataSelector)
		return yield callAny(
			myLibrarySync.launchSession,
			presentationUID,
			true,
			userData.jwt,
			sessionType
		)
	} catch (error) {
		throw formatError(error)
	}
}

function *launchFrontOfTheClassroom(action) {
	try {
		const libraryLessonSelector = yield select(
			myLibrarySelectors.myLibraryLessonSelector,
			{entityUID: action.presentation_uid}
		)
		let presentationUid = ''
		if (libraryLessonSelector.data.inLibrary) {
			presentationUid = action.presentation_uid
		} else {
			presentationUid = yield call(saveLessonBeforeLaunch, action.presentation_uid)
		}
		const response = yield call(launchSession, presentationUid, sessionTypes.FRONT_OF_CLASSROOM)
		yield put(myLibraryActions.launchFrontOfTheClassroomSessionFetched(response))
	} catch (serverError) {
		yield put(myLibraryActions.launchFrontOfTheClassroomSessionFailed(serverError))
	}
}

function *handleFailedSaveLesson(action) {
	if (action.error.error_code === errorCodes.GOOGLE_SLIDES_SAVE_ALREADY_PUBLISHED) {
		const entity = action.entity
		yield call(getPreviewData, entity.data.entityUID, 'uid')
	}
}

function *getActivePinCodes(action) {
	try {
		const userData = yield select(myLibraryUserDataSelector)
		const response = yield callAny(
			myLibrarySync.getActivePincodes,
			action.entity.data.presentation_uid,
			userData.jwt,
		)
		yield put(myLibraryActions.getActivePincodesSuccess(response.payload))
	} catch (error) {
		const serverError = formatError(error)
		yield put(myLibraryActions.getActivePincodesFailed(serverError))
	}


}

export default [
	fork(getLibraryPage),
	takeLatest([
		actionTypes.MY_LIBRARY_INITIALIZE,
		actionTypes.MY_LIBRARY_LESSONS_PICKER_SAVE_LESSONS_SUCCESS,
		actionTypes.MY_LIBRARY_ROUTE_UPDATE,
		actionTypes.MY_LIBRARY_DELETE_FOLDER_RESPONSE_SUCCESS,
		actionTypes.MY_LIBRARY_REMOVE_LESSON_FROM_FOLDER_SUCCESS,
		actionTypes.MY_LIBRARY_MOVE_LESSON_SUCCESS,
		actionTypes.MY_LIBRARY_MOVE_FOLDER_SUCCESS,
	], getMyLibraryFolders),
	takeLatest([
		actionTypes.MY_LIBRARY_LAUNCH_LIVE_LESSON,
		actionTypes.MY_LIBRARY_LAUNCH_NEW_LIVE_SESSION,
		actionTypes.MY_LIBRARY_HAS_MULTIPLE_LIVE_SESSIONS,
		actionTypes.GOALS_COLLABORATE_CARD_LAUNCH_LIVE_LESSON,
		actionTypes.GOALS_TIME_TO_CLIMB_CARD_LAUNCH_LIVE_LESSON,
		actionTypes.GOALS_OEQ_CARD_LAUNCH_LIVE_LESSON,
		actionTypes.MY_LIBRARY_PREVIEW_LAUNCH_ALL_DEVICES,
		actionTypes.PRIVATE_LIBRARY_PREVIEW_LAUNCH_ALL_DEVICES,
		actionTypes.STORE_PREVIEW_LAUNCH_ALL_DEVICES,
	], launchMyLibraryLiveLesson),
	takeLatest([
		actionTypes.MY_LIBRARY_LAUNCH_HOMEWORK,
		actionTypes.MY_LIBRARY_LAUNCH_NEW_HOMEWORK,
		actionTypes.MY_LIBRARY_PREVIEW_LAUNCH_STUDENT_PACED,
		actionTypes.STORE_PREVIEW_LAUNCH_STUDENT_PACED,
		actionTypes.PRIVATE_LIBRARY_PREVIEW_LAUNCH_STUDENT_PACED,
	], launchMyLibraryHomework),
	takeLatest([
		actionTypes.MY_LIBRARY_LAUNCH_HOMEWORK_COLLABORATE_DISABLED_LAUNCH,
	], launchHomeworkWithCollaborateDisabled),
	takeEvery(actionTypes.MY_LIBRARY_DELETE_LESSON_CONFIRMED, deleteLesson),
	takeLatest([actionTypes.MY_LIBRARY_SAVE_CHANGES, actionTypes.MY_LIBRARY_LESSON_DATA_SAVED], saveChanges),
	takeLatest([actionTypes.MY_LIBRARY_ADD_LESSON_TO_PUBLIC_LIBRARY], addToPublicLibrary),
	takeLatest([actionTypes.MY_LIBRARY_ADD_LESSON_TO_FOLDER_SELECTED], addToFolder),
	takeLatest([actionTypes.MY_LIBRARY_REMOVE_LESSON_FROM_FOLDER_CONFIRMED], removeFromFolder),
	takeLatest(actionTypes.MY_LIBRARY_EXPORT_TO_LOCAL_DRIVE, getLessonPdfUrl),
	takeLatest([actionTypes.MY_LIBRARY_ADD_OR_UPDATE_FOLDER], addOrUpdateFolder),
	takeLatest([actionTypes.MY_LIBRARY_ADD_LESSON_TO_PRIVATE_LIBRARY,
		actionTypes.MY_LIBRARY_ADD_LESSON_TO_PRIVATE_LIBRARY_MODAL_ADD_SELECTED], addToPrivateLibrary),
	takeLatest([actionTypes.MY_LIBRARY_DELETE_FOLDER_CONFIRMED], removeFolder),
	fork(getLessonsStatus),
	takeLatest([actionTypes.MY_LIBRARY_DUPLICATE_LESSON], duplicateLesson),
	takeLatest([actionTypes.MY_LIBRARY_DUPLICATE_LESSON_CONFIRMED], duplicateLessonConfirmed),
	takeLatest([
		actionTypes.MY_LIBRARY_EDIT_LESSON,
		actionTypes.MY_LIBRARY_EDIT_RUNNING_LESSON,
		actionTypes.MY_LIBRARY_EDIT_STORE_LESSON,
		actionTypes.MY_LIBRARY_EDIT_LESSON_IN_PUBLIC_LIBRARY,
		actionTypes.STORE_EDIT_LESSON,
		actionTypes.MY_LIBRARY_EDIT_GOOGLE_SLIDES_IN_NEARPOD,
	], editLesson),
	fork(lessonsPickerSearchLessons),
	takeLatest(actionTypes.MY_LIBRARY_LESSONS_PICKER_SAVE_LESSONS, lessonsPickerSaveLessons),
	fork(watchPreviewRoute),
	takeLatest(actionTypes.MY_LIBRARY_SHARE_LESSON, getMyLibraryLessonShareData),
	takeLatest(actionTypes.MY_LIBRARY_SHARE_FOLDER, getMyLibraryShareFolderData),
	takeEvery(actionTypes.MY_LIBRARY_SESSION_CODE_SAVE_EXPIRATION_DATE, saveExpirationDate),
	takeLatest(actionTypes.MY_LIBRARY_CHANGE_FOLDER_COLOR, changeFolderColor),
	takeEvery(actionTypes.MY_LIBRARY_PENDING_LESSONS_STATUS_FETCHED, updateLessonData),
	takeLatest(actionTypes.MY_LIBRARY_MOVE_LESSON, moveLesson),
	takeLatest(actionTypes.MY_LIBRARY_MOVE_FOLDER, moveFolder),
	takeLatest([
		actionTypes.MY_LIBRARY_CREATE_GOOGLE_DRIVE_LESSON,
		actionTypes.MAIN_TOPBAR_CREATE_GOOGLE_DRIVE_LESSON,
	], createGoogleDriveLesson),
	takeLatest([
		actionTypes.MY_LIBRARY_LAUNCH_FRONT_OF_THE_CLASSROOM,
		actionTypes.MY_LIBRARY_PREVIEW_LAUNCH_FRONT_OF_THE_CLASSROOM,
		actionTypes.STORE_PREVIEW_LAUNCH_FRONT_OF_THE_CLASSROOM,
	], launchFrontOfTheClassroom),
	takeEvery(actionTypes.MY_LIBRARY_STUDENT_PACED_TOGGLE_REQUIRED_SUBMISSIONS, saveSessionSubmissionRequired),
	takeEvery(actionTypes.GOOGLE_SLIDES_SAVE_PRESENTATION_PROGRESSIVE_START_FAILED, handleFailedSaveLesson),
	takeLatest(actionTypes.MY_LIBRARY_TEACH_LESSON_SELECTED, getActivePinCodes),
]

export {
	launchMyLibraryLiveLesson,
	launchMyLibraryHomework,
	getMyLibraryShareFolderData,
	removeFolder,
	addOrUpdateFolder,
	moveLesson,
	moveFolder,
	createGoogleDriveLesson,
	duplicateLesson,
	duplicateLessonConfirmed,
	saveLessonBeforeLaunch,
	handleFailedSaveLesson,
	launchSession,
	launchFrontOfTheClassroom,
	getPreviewData,
	addToPrivateLibrary,
}
